/**
 * useCompactLayout — décide si l'écran s'affiche en version portrait
 * (caisse haut / bas, tablette serveur / tables, plan de salle…).
 *
 * Portrait = hauteur > largeur, quelle que soit la largeur (TPE, téléphone,
 * tablette debout). Le clavier Android ne fait pas basculer la mise en page
 * (décision figée pendant une saisie, cf. nextPortraitState). Flag commerce
 * `caisse_affichage_compact` + préférence d'appareil.
 *
 * Sous 600 dp de large, la classe `vp-compact` est posée sur <html> : elle
 * passe les fenêtres (Dialog) en plein écran via index.css.
 *
 * Présentation seule : aucun effet sur le panier, l'encaissement ou le fiscal.
 */
import { useEffect, useRef, useState } from 'react';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { useLocalStorageState } from '@/hooks/useLocalStorageState';
import {
  COMPACT_DEVICE_PREF_KEY,
  COMPACT_MAX_WIDTH,
  COMPACT_MEDIA_QUERY,
  nextPortraitState,
  parseDevicePref,
  resolveCompactLayout,
  type CompactDevicePref,
} from '@/lib/compact-layout';

const COMPACT_HTML_CLASS = 'vp-compact';

const isEditing = (): boolean => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
};

const sample = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
  editing: isEditing(),
});

/** Écran en portrait, insensible au clavier virtuel. */
export function usePortraitScreen(): boolean {
  const last = useRef<{ portrait: boolean; width: number } | null>(null);
  const [portrait, setPortrait] = useState(() =>
    typeof window === 'undefined' ? false : nextPortraitState(null, sample()),
  );
  useEffect(() => {
    const update = () => {
      const s = sample();
      const next = nextPortraitState(last.current, s);
      last.current = { portrait: next, width: s.width };
      setPortrait(next);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return portrait;
}

/** Largeur < 600 dp (fenêtres en plein écran). */
/**
 * PDA ou téléphone, quel que soit le sens de l'appareil : le plus petit côté de l'écran est étroit.
 * Une caisse fixe ou une tablette restent exclues.
 */
export function useHandheldDevice(): boolean {
  const [handheld] = useState(
    () =>
      typeof window !== 'undefined' &&
      Math.min(window.screen.width, window.screen.height) <= COMPACT_MAX_WIDTH,
  );
  return handheld;
}

export function useNarrowScreen(): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(COMPACT_MEDIA_QUERY).matches
      : false;
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(COMPACT_MEDIA_QUERY);
    const onChange = () => setNarrow(mql.matches);
    onChange();
    // Vieilles WebView : addListener à défaut d'addEventListener sur MediaQueryList.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return narrow;
}

export type CompactLayout = {
  /** Mise en page portrait active. */
  compact: boolean;
  /** Écran en portrait, indépendamment des réglages. */
  isPortrait: boolean;
  /** Largeur < 600 dp. */
  isNarrow: boolean;
  /** Le commerce autorise l'affichage compact. */
  tenantEnabled: boolean;
  devicePref: CompactDevicePref;
  setDevicePref: (pref: CompactDevicePref) => void;
};

export function useCompactLayout(): CompactLayout {
  const isPortrait = usePortraitScreen();
  const isNarrow = useNarrowScreen();
  const { isFeatureEnabled } = useFeatureToggles();
  const tenantEnabled = isFeatureEnabled('caisse_affichage_compact');
  const [rawPref, setRawPref] = useLocalStorageState<CompactDevicePref>(
    COMPACT_DEVICE_PREF_KEY,
    'auto',
  );
  const devicePref = parseDevicePref(rawPref);
  const compact = resolveCompactLayout({ isPortrait, tenantEnabled, devicePref });
  const fullscreenDialogs = compact && isNarrow;

  useEffect(() => {
    if (!fullscreenDialogs) return undefined;
    const root = document.documentElement;
    root.classList.add(COMPACT_HTML_CLASS);
    return () => root.classList.remove(COMPACT_HTML_CLASS);
  }, [fullscreenDialogs]);

  return { compact, isPortrait, isNarrow, tenantEnabled, devicePref, setDevicePref: setRawPref };
}
